import { useCallback } from 'react';
import {
  type NavigateOptions,
  type To,
  useLocation,
  useNavigate,
} from 'react-router';
import { useAopTopBar } from 'microFrontend/hooks/useAopTopBar';
import { toSuitePath } from 'microFrontend/router/suites/legacyRedirects';
import { organizationSelector } from 'slices/organization.slice';
import { useAppSelector } from 'slices/store';
import { parseCustomerSignature } from 'utils/customerSignature';

export function useCustomerNavigate() {
  const navigate = useNavigate();
  const location = useLocation();
  const organization = useAppSelector(organizationSelector);
  const customerSignature = organization?.customerSignature;
  const isAop = useAopTopBar();

  return useCallback(
    (to: To, options?: NavigateOptions) => {
      if (!customerSignature) {
        return navigate(to, options);
      }

      const customerSignaturePrefix = `/${parseCustomerSignature(
        customerSignature
      )}`;

      // Pages link to their legacy URLs. In the AOP router those live in a
      // suite, so go there directly instead of through a redirect route
      const withPrefix = (path: string) => {
        const pathname = path.startsWith(customerSignaturePrefix)
          ? path
          : `${customerSignaturePrefix}${path}`;
        if (!isAop) return pathname;
        const rest = pathname.slice(customerSignaturePrefix.length);
        return `${customerSignaturePrefix}${toSuitePath(rest)}`;
      };

      const adminStartPage = withPrefix('/configuration/general-settings');

      if (typeof to === 'string') {
        const pathname = withPrefix(to);

        // Path for which route does not exist, go back to admin default page
        if (pathname === location.pathname) {
          return navigate(adminStartPage);
        }

        return navigate(pathname, options);
      }

      if (!to.pathname) {
        return navigate(to, options);
      }

      const pathname = withPrefix(to.pathname);

      // Path for which route does not exist, go back to admin default page
      if (pathname === location.pathname) {
        return navigate(adminStartPage);
      }

      return navigate({ ...to, pathname }, options);
    },
    [customerSignature, isAop, location.pathname, navigate]
  );
}
